import React, { createRef, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { addOverlay, removeOverlay } from '../Actions/Overlay.action';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Thumbs, FreeMode, Zoom, Navigation } from 'swiper';
import 'swiper/css/bundle';
import 'swiper/css/zoom';
import Icon from './Icon';
import { translate } from '../Services/translation';

const prevElementId = 'swiper-prev-el';
const nextElementId = 'swiper-next-el';

const FullscreenImageSlider = ({
  showCloseButton = true,
  images,
  initialSlide = 0,
  onClosed,
}) => {
  const dispatch = useDispatch();
  const contentRef = createRef();
  const closeRef = useRef(null);

  const [open, setOpen] = useState(true);
  const [isZoomed, setIsZoomed] = useState(false);
  const [hasLoadedOriginalImage, setHasLoadedOriginalImage] = useState([]);

  const onKeyDown = (e) => {
    if (e.key == 'Escape') {
      setOpen(false);
    }
  };

  useEffect(() => {
    dispatch(
      addOverlay('FullscreenImageSlider', () => {
        setOpen(false);
      })
    );
    document.addEventListener('keydown', onKeyDown, false);

    return () => {
      document.removeEventListener('keydown', onKeyDown, false);
    };
  }, [dispatch]);

  useEffect(() => {
    if (!open) {
      dispatch(removeOverlay('FullscreenImageSlider'));
      onClosed && onClosed();
    }
  }, [dispatch, open, onClosed]);

  useEffect(() => {
    closeRef.current?.focus(); // Adds focus to closeButton when opened
  }, []);

  const onZoom = (slideIndex, scale) => {
    setIsZoomed(scale > 1);
    if (!hasLoadedOriginalImage.includes(slideIndex)) {
      setHasLoadedOriginalImage((hasLoadedOriginalImage) => [
        ...hasLoadedOriginalImage,
        slideIndex,
      ]);
    }
  };

  return open ? (
    <div className="fullscreen-image__section">
      {showCloseButton && (
        <button
          className="fullscreen-image__close"
          onClick={() => setOpen(false)}
          ref={closeRef}
          aria-label={translate('product.image.close-fullscreen.sc')}
        >
          <Icon
            name="Close"
            className="fullscreen-image__close-icon"
            size={24}
          />
        </button>
      )}
      <div className="fullscreen-image__swiper-wrapper" ref={contentRef}>
        <button
          className="swiper-button-prev"
          id={prevElementId}
          aria-label={translate('product.image.previous.sc')}
        >
          <Icon name="Caret-Left" className="icon" size={24} />
        </button>
        <Swiper
          className="fullscreen-image__swiper"
          loop={true}
          slidesPerView={1}
          initialSlide={initialSlide}
          zoom={{
            toggle: true,
          }}
          navigation={{
            enabled: true,
            prevEl: `#${prevElementId}`,
            nextEl: `#${nextElementId}`,
          }}
          modules={[Pagination, Thumbs, FreeMode, Navigation, Zoom]}
          onZoomChange={(swiper, scale) => onZoom(swiper.realIndex, scale)}
          pagination={{
            type: 'fraction',
          }}
        >
          {images.map((img, idx) => (
            <SwiperSlide
              key={'fullwidth-image-slide' + idx + img.hdSrc}
              zoom={true}
            >
              <div className="fullscreen-image__image-wrapper">
                <div className="swiper-zoom-container">
                  <img
                    className="fullscreen-image__image"
                    src={
                      isZoomed || hasLoadedOriginalImage.includes(idx)
                        ? img.originalSrc
                        : img.hdSrc
                    }
                    loading={idx == 0 ? 'eager' : 'lazy'}
                  />
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        <button
          className="swiper-button-next"
          id={nextElementId}
          aria-label={translate('product.image.previous.sc')}
        >
          <Icon name="Caret-Right" className="icon" size={24} />
        </button>
      </div>
    </div>
  ) : null;
};

export default FullscreenImageSlider;
